import React, { useState } from 'react';
import { Button, Modal, Table, Spinner, Alert } from 'react-bootstrap';
import { stockItemAPI } from '../api/stockItemAPI';
import { formatQuantity } from '../utils/commonUtils';

const badgeBtnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    borderRadius: '20px',
    background: '#cff4fc',
    border: '1px solid #b6effb',
    color: '#055160',
    fontSize: '0.8rem',
    cursor: 'pointer',
    transition: 'background 0.15s, border-color 0.15s',
    whiteSpace: 'nowrap',
};

const CostHistoryButton = ({ stockItemId, stockItemDescription }) => {
    const [show, setShow] = useState(false);
    const [costHistory, setCostHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [loaded, setLoaded] = useState(false);

    const handleShow = async () => {
        setShow(true);

        // Only fetch if not already loaded
        if (!loaded && stockItemId) {
            setLoading(true);
            setError(null);

            try {
                const data = await stockItemAPI.getCostHistory(stockItemId);
                setCostHistory(data || []);
                setLoaded(true);
            } catch (err) {
                console.error('Error fetching cost history:', err);
                setError('Failed to load cost history');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleClose = () => setShow(false);

    // Format date to a readable format
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Format currency
    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return 'N/A';
        return `$${parseFloat(amount).toFixed(2)}`;
    };

    if (!stockItemId) {
        return null;
    }

    return (
        <>
            <button
                type="button"
                onClick={handleShow}
                className="ms-2"
                title="View cost history"
                style={badgeBtnStyle}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#9eeaf9'; e.currentTarget.style.borderColor = '#6edff6'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#cff4fc'; e.currentTarget.style.borderColor = '#b6effb'; }}
            >
                <i className="bi bi-clock-history"></i> History
            </button>

            <Modal show={show} onHide={handleClose} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '1.1rem' }}>
                        Historical Cost Data
                        {stockItemDescription && (
                            <div className="text-muted mt-1" style={{ fontSize: '0.875rem', fontWeight: 'normal' }}>
                                {stockItemDescription}
                            </div>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loading && (
                        <div className="text-center py-4">
                            <Spinner animation="border" size="sm" />
                            <div className="mt-2 text-muted" style={{ fontSize: '0.875rem' }}>Loading cost history...</div>
                        </div>
                    )}

                    {!loading && error && (
                        <Alert variant="warning" style={{ fontSize: '0.875rem' }}>
                            {error}
                        </Alert>
                    )}

                    {!loading && !error && costHistory.length === 0 && (
                        <Alert variant="info" style={{ fontSize: '0.875rem' }}>
                            No cost history available for this item yet.
                        </Alert>
                    )}

                    {!loading && !error && costHistory.length > 0 && (
                        <div>
                            <div className="mb-2 text-muted" style={{ fontSize: '0.875rem' }}>
                                Showing last {costHistory.length} receive{costHistory.length !== 1 ? 's' : ''}
                            </div>
                            <Table bordered hover size="sm" style={{ fontSize: '0.875rem' }}>
                                <thead className="table-light">
                                    <tr>
                                        <th style={{ width: '130px' }}>Date</th>
                                        <th style={{ width: '120px' }}>Unit Cost</th>
                                        <th style={{ width: '100px' }}>Quantity</th>
                                        <th>Vendor</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {costHistory.map((record, index) => (
                                        <tr key={index}>
                                            <td>{formatDate(record.received_date)}</td>
                                            <td className="fw-bold">{formatCurrency(record.cost_per_unit)}</td>
                                            <td>{formatQuantity(record.quantity)}</td>
                                            <td>{record.vendor_name || 'N/A'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default CostHistoryButton;
